/**
 * Color de acento del formulario de solicitud: el del botón del formulario de contacto de la web.
 * Si no se reconoce o es tan claro que no se leería sobre blanco, se usa el color principal del tema.
 */

export const ACENTO_DEFECTO = '#1f3a5f'

/** Color en [r, g, b] (0–255) a partir de #rgb, #rrggbb o rgb()/rgba(); null si no se reconoce. */
export function rgbDe(color: string): [number, number, number] | null {
  const c = (color || '').trim().toLowerCase()
  let m = c.match(/^#([0-9a-f]{3})$/)
  if (m) return m[1].split('').map((x) => parseInt(x + x, 16)) as [number, number, number]
  m = c.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/)
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number]
  m = c.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+%?))?\s*\)$/)
  if (m) {
    const alfa = m[4] === undefined ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4])
    if (alfa < 0.6) return null // casi transparente: no sirve como fondo
    return [+m[1], +m[2], +m[3]]
  }
  return null
}

/** Luminancia relativa (WCAG) de 0 (negro) a 1 (blanco). */
export function luminancia([r, g, b]: [number, number, number]) {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4) }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

/** Acento utilizable: el color de la web si se lee bien sobre blanco; si no, el por defecto. */
export function acentoLegible(color: string | null | undefined): string {
  const rgb = rgbDe(color || '')
  if (!rgb || luminancia(rgb) > 0.6) return ACENTO_DEFECTO
  return color as string
}

/** Texto legible sobre el acento (blanco u oscuro según su claridad). */
export function textoSobre(color: string): string {
  const rgb = rgbDe(color)
  return rgb && luminancia(rgb) > 0.4 ? '#1d2329' : '#ffffff'
}

/**
 * El color de texto elegido en el panel si se lee sobre el fondo (contraste ≥ 3, el mínimo WCAG para texto grande y negrita); si no, blanco u oscuro.
 * Evita botones "sin texto" cuando el panel tiene, por ejemplo, texto azul oscuro sobre fondo azul oscuro.
 */
export function textoLegibleSobre(fondo: string, texto: string | null | undefined): string {
  const f = rgbDe(fondo)
  const t = rgbDe(texto || '')
  if (f && t) {
    const [a, b] = [luminancia(f), luminancia(t)].sort((x, y) => y - x)
    if ((a + 0.05) / (b + 0.05) >= 3) return texto as string
  }
  return textoSobre(fondo)
}

/** Alfa (0–1) de un color #rgb, #rrggbb o #rrggbbaa; 1 si no lo indica. */
function alfaDe(color: string): number {
  const m = (color || '').trim().match(/^#[0-9a-f]{6}([0-9a-f]{2})$/i)
  return m ? parseInt(m[1], 16) / 255 : 1
}

export interface EstiloBotonEncargo { propio: boolean; fondo: string; texto: string; borde: string; acento: string }

/**
 * Colores de los botones del encargo online: los elegidos en el panel (SolicitudWeb.Boton) y, para lo que esté vacío,
 * los de la web (acento). El texto siempre se lee sobre el fondo y, si el fondo se lee sobre blanco, también es el acento
 * del resto del formulario (barra de progreso, pasos, enlaces). Ref: https://trello.com/c/16vODf98
 */
export function estiloBotonEncargo(boton: any, acentoWeb: string): EstiloBotonEncargo {
  const valido = (c: any) => typeof c === 'string' && !!rgbDe(c)
  const fondoPanel = valido(boton?.BackgroundColor) ? String(boton.BackgroundColor) : ''
  const fondo = fondoPanel || acentoWeb
  // Fondo casi transparente (botón solo con borde): el texto se compara con el blanco de la página
  const transparente = alfaDe(fondo) < 0.6
  const texto = textoLegibleSobre(transparente ? '#ffffff' : fondo, valido(boton?.Color) ? boton.Color : undefined)
  const borde = valido(boton?.BorderColor) ? String(boton.BorderColor) : (transparente ? texto : fondo)
  const acento = fondoPanel && !transparente && acentoLegible(fondoPanel) === fondoPanel ? fondoPanel : acentoWeb
  const propio = !!fondoPanel || valido(boton?.Color) || valido(boton?.BorderColor)
  return { propio, fondo, texto, borde, acento }
}
